// cleanup_hoc_to_hooks_PAYMENT_MAPS
// cleanup_remove_external_libraries_PAYMENT_MAPS
// cleanup_convert_to_typescript_file_PAYMENT_MAPS

/* eslint-disable react/jsx-indent */
import React, { useRef, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { camelCase, debounce, get, template } from 'lodash';
import classnames from 'classnames';

import Alert from '@glu/alerts-react';
import { SECONDARY, TERTIARY } from '@glu/buttons-react';
import { ContainerArrowDownNextIcon, InfoCircleAshIcon } from '@glu/icons-react';
import { withFormProvider } from '@glu/validation-react';
import { Dropdown } from '@glu/dropdown-react';
import { BareCheckboxRadio as Checkbox, ValidatedInput as Input } from '@glu/form-components';
import { Tooltip } from '@glu/tooltip-react';

import ThemedLink from 'components/themedLink/ThemedLink';
import { lt } from 'utils/validations/gluValidations';
import { unsubscribeComponent } from 'utils/unsubscribeComponent';
import { ACTIONS, ALERTVARIATIONS, DEFAULT_PROPS, WORKSPACES, RESPONSES } from 'constants.js';
import Button from 'components/gluValidation/Button';
import useLocale from 'components/useLocale';
import PlaceResolver, { variants } from 'components/placeResolver/PlaceResolver';
import RadioButton from 'components/radioButton/RadioButton';
import Spinner from 'components/spinner/Spinner';
import {
  getWireTypes,
  getWireImportDefaultFields,
  getDateFormats,
  getSeparators,
  getDelimiters,
  isDuplicateMapName,
  isValidAccountNumber,
  isValidDate,
  isValidBankCode,
  createWireImportMap,
  modifyWireImportMap,
  getEndOfRecordOptions,
  getCharges,
  getWirePreferences
} from 'api/payments/service';
import { retrieveWireImportMap } from 'api/wireImports/service';
import countryCodeListFiles from 'components/widgets/contactCenterListView/PayeeImport/countryCodeList.csv';

import useStyles from './CreateWireImportMap.styles';
import { TRANSCRIPTS_WIRE_TYPES } from '../utils';

const { MODIFY } = ACTIONS;

const COUNTRY_FIELD = 'Beneficiary Account Country';
const IBAN_ACCOUNT_TYPE = 'Account type IBAN';
const COUNTRY_CODE_LIST = 'CountryCodeList.csv';

const {
  SINGLE_CHARACTER: { NO, YES }
} = RESPONSES;

const { EMPTY_FUNCTION, FALSE } = DEFAULT_PROPS;

const DEBOUNCE_TIMEOUT = 300;

const validateMapName = async (name, data, apiData) => {
  const value = data[name] || '';
  if (value.length < 1 || apiData?.header?.mapName === value) {
    return true;
  }
  const results = await isDuplicateMapName({ mapName: value }).toPromise();
  return !results?.duplicate;
};

const validateDate = debounce(async (name, _, data) => {
  const date = data[name] || '';
  if (date.length === 0) {
    return true;
  }
  const dateFormat = data.sendDateSpecialFormatting?.[0]?.name;
  const { success: valid } = await isValidDate({
    name,
    dateFormat,
    date
  }).toPromise();

  return valid;
}, DEBOUNCE_TIMEOUT);

const validateBankCode = debounce(async (name, _, data) => {
  const value = data[name] || '';
  if (value.length === 0) {
    return true;
  }
  const { valid } = await isValidBankCode({
    name,
    beneficiaryBankCode: value
  }).toPromise();

  return valid;
}, DEBOUNCE_TIMEOUT);

const validateAccountNumber = debounce(async (name, _, data) => {
  const value = data[name] || '';
  if (value.length === 0) {
    return true;
  }
  const apiCall = await isValidAccountNumber({ name, accountNumber: value }).toPromise();
  const { valid } = apiCall;
  return valid;
}, DEBOUNCE_TIMEOUT);

const hasEitherPositionOrReplacement = (f) => (name, _, data) => {
  const hasPositionValue = (data?.[`${f}FieldPosition`] || '').length !== 0;
  const hasReplacementValue = (data?.[`${f}ReplacementValue`] || '').length !== 0;
  return hasPositionValue || hasReplacementValue;
};

const CreateWireImportMapComponent = ({
  data: formState,
  modify = FALSE,
  onChange: formChange,
  setLeavingPage = EMPTY_FUNCTION,
  setLoadingForWiresApi = EMPTY_FUNCTION,
  setPaymentType = EMPTY_FUNCTION,
  setSending = EMPTY_FUNCTION,
  user,
  validate: formValidate,
  wireType
}) => {
  const locale = useLocale();
  const history = useHistory();
  const { location } = history;
  const { id } = useParams();

  const classes = useStyles();

  const [error, setError] = useState(null);
  const [validations, setValidations] = useState({ fields: {} });
  const [apiResponse, setApiResponse] = useState(null);
  const [wirePreferences, setWirePreferences] = useState({});
  const [initial, setInitial] = useState(true);
  const [duplicates, setDuplicates] = useState([]);
  const { createdBy } = location?.state || {};
  const prevUrl = location?.state?.previous || WORKSPACES.PYMTCNTR;

  const refs = {
    debitAccountNumber: useRef(),
    beneficiaryBankCode: useRef(),
    sendDate: useRef()
  };

  const [fieldsData, setFieldsData] = useState({
    selectall: {
      trimLeadingBlanks: false,
      trimLeadingZeros: false
    },
    fields: {}
  });

  const [api, setApiState] = useState({
    loading: true
  });
  const [subscriptions, setSubscriptions] = useState({});

  const initialize = () => {
    const { paymentType } = location.state || {};

    // wire type match
    const match = apiResponse.wireTypes.find((t) => t.value === formState.wireType);

    const requests = {
      modify: () =>
        retrieveWireImportMap({
          action: MODIFY,
          mapId: parseInt(id, 10),
          paymentType: initial ? paymentType : match && match.label
        }),
      create: () => getWireImportDefaultFields({ paymentType: formState.wireType })
    };

    const fields$ = modify ? requests.modify() : requests.create();
    const sub$ = fields$.subscribe(
      ({
        data: {
          isISOFormatEnabled,
          wireImportMapDetail = [],
          wireImportMapFields = [],
          wireImportMapHeader: header = {}
        }
      }) => {
        const data = modify ? wireImportMapDetail : wireImportMapFields;

        const type = modify
          ? (initial &&
              get(
                apiResponse.wireTypes.find((x) => x.label === header.wireType),
                'value'
              )) ||
            formState.wireType
          : formState.wireType;

        setApiState({
          loading: false,
          type,
          isISOFormatEnabled,
          header,
          fields: data.filter((f) => f.isDisplay === YES),
          requiredFields: data
            .filter((x) => x.required === YES)
            .map((f) => camelCase(f.fieldName)),
          fieldNames: data.filter((x) => x.isDisplay === YES).map((x) => camelCase(x.fieldName))
        });

        setInitial(false);

        Object.assign(formState, {
          skipHeaderRecord: header ? header.skipHeaderRecord : 0,
          skipHeaderRecordValue: header ? header.skipHeaderRecord : 0,
          delimiter: [apiResponse?.delimiters?.source?.[0]],
          endOfRecordMethod: get(header, 'endofRecordMethod')
            ? [
                get(apiResponse, 'endOfRecord.source', []).find(
                  (x) => x?.id?.toLowerCase() === header.endofRecordMethod?.toLowerCase()
                )
              ]
            : [get(apiResponse, 'endOfRecord.source.0')]
        });

        let blanks = 0;
        let zeros = 0;
        Object.keys(data).forEach((k) => {
          const field = camelCase(data[k].fieldName);
          const {
            amountDecimal,
            fieldPosition,
            fileSeparator,
            replacementValue,
            specialFormatting,
            trimLeadingBlanks,
            trimLeadingZeros
          } = data.find((x) => camelCase(x.fieldName) === field);

          if (data[k].trimLeadingBlanks > 0) {
            blanks += 1;
          }

          if (data[k].trimLeadingZeros > 0) {
            zeros += 1;
          }

          Object.assign(formState, {
            [`${field}AmountDecimal`]: amountDecimal || '',
            [`${field}FieldPosition`]: fieldPosition || '',
            [`${field}FileSeparatorVisible`]:
              !!fileSeparator && fileSeparator.toUpperCase() !== RESPONSES.NO,
            [`${field}ReplacementValue`]:
              field === 'detailOfCharges'
                ? (replacementValue && [
                    apiResponse.charges.source.find((x) => x.id === replacementValue)
                  ]) ||
                  []
                : replacementValue,
            [`${field}TrimLeadingBlanks`]: trimLeadingBlanks,
            [`${field}SpecialFormatting`]: specialFormatting
              ? [
                  apiResponse.dateFormats &&
                    apiResponse.dateFormats.source.find(
                      (x) => x.name.toLowerCase() === specialFormatting.toLowerCase()
                    )
                ]
              : null,
            [`${field}TrimLeadingZeros`]: trimLeadingZeros
          });
        });

        const fieldsCount = data.filter((f) => f.isDisplay === YES).length;

        if (blanks === fieldsCount) {
          Object.assign(formState, {
            trimLeadingBlanks: true
          });
        }

        if (zeros === fieldsCount) {
          Object.assign(formState, {
            trimLeadingZeros: true
          });
        }

        if (modify) {
          Object.assign(formState, {
            mapName: header.mapName,
            delimiter: [apiResponse?.delimiters?.source.find((x) => x.code === header.delimiter)],
            endOfRecordMethod: [
              get(apiResponse, 'endOfRecord.source', []).find(
                (x) => x.code.toLowerCase() === header.endofRecordMethod.toLowerCase()
              ) || [get(apiResponse, 'endOfRecord.source.0')]
            ].filter(Boolean),
            stripQuotes: header.stripQuotes,
            wireType: type,
            skipHeaderRecord: header.skipHeaderRecord
          });
        }

        formChange('update', true);

        setFieldsData((fieldsData) => ({
          ...fieldsData,
          fields: data
            .map((o) => ({ [camelCase(o.fieldName)]: o }))
            .reduce((acc, o) => ({ ...acc, ...o }))
        }));
      }
    );

    setSubscriptions({ sub$ });
  };

  useEffect(
    () => {
      setLeavingPage(true);

      const wirePrefSubscription = getWirePreferences().subscribe((data) => {
        setWirePreferences(data);
      });

      setSubscriptions({ wirePrefSubscription });

      return () => {
        setLeavingPage(false);
        unsubscribeComponent(subscriptions);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!formState.wireType) {
      return;
    }

    if (formState.wireType !== api.type) {
      setApiState((state) => ({ ...state, loading: true }));
      initialize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.wireType]);

  useEffect(() => {
    if (!formState.sendDateSpecialFormatting) {
      return;
    }

    const sendDateField = refs.sendDate && refs.sendDate.current;
    const sendDateReplacementValue = formState.sendDateReplacementValue;

    if (sendDateField && sendDateReplacementValue) {
      sendDateField.focus();
      sendDateField.blur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.sendDateSpecialFormatting]);

  const checkForDuplicates = () => {
    const fieldPositons = Object.entries(formState).reduce(
      (acc, currentValue) => {
        const [key, fieldValue] = currentValue;
        const stringifiedFieldValue = String(fieldValue);
        if (key.includes('FieldPosition') && stringifiedFieldValue.length > 0) {
          acc.byValue[stringifiedFieldValue] = acc.byValue[stringifiedFieldValue] || [];
          acc.byValue[stringifiedFieldValue].push(key);

          if (acc.byValue[fieldValue].length > 1) {
            acc.duplicates = [...acc.duplicates, ...acc.byValue[fieldValue]];
          }
        }
        return acc;
      },
      { byValue: {}, duplicates: [] }
    );

    setDuplicates(fieldPositons.duplicates);
  };

  const fieldPositions = Object.entries(formState).filter(([key]) =>
    key.includes('FieldPosition')
  );

  const stringifiedFieldPositions = JSON.stringify(fieldPositions);

  useEffect(() => {
    checkForDuplicates();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringifiedFieldPositions]);

  const validatePositionAndReplacement = (f) => {
    formValidate(`${f}FieldPosition`);
    formValidate(`${f}ReplacementValue`);
  };

  const onChange =
    (type, options = {}) =>
    (data, value, add) => {
      const events = {
        radio: ({ name, value }) => {
          formChange(name, value);
        },
        checkbox: (name, checked) => {
          const { isYesNo } = options;
          const yesno = checked ? YES : NO;

          if (name === 'skipHeaderRecord') {
            formChange('skipHeaderRecordValue', 1, true);
          }
          formChange(name, isYesNo ? yesno : +checked);
        },
        dropdown: (name, value) => {
          const fields = ['endOfRecordMethod', 'delimiter'];
          const isDelimiterOrEndOfRecord = ['endOfRecordMethod', 'delimiter'].includes(name);

          if (isDelimiterOrEndOfRecord) {
            const opposite = name === 'endOfRecordMethod' ? fields[1] : fields[0];
            const values = {
              opposite: get(formState, `${opposite}.0.id`),
              current: get(value, '0.id')
            };

            if (values.opposite !== values.current) {
              formValidate(opposite);
            }
          } else {
            formChange(name, value);
          }
        },
        input: (name, value) => {
          if (name === 'skipHeaderRecordValue') {
            formChange('skipHeaderRecord', +value);
          }
          if (name !== 'mapName') {
            formValidate(name);
          }
          formChange(name, value);
        },
        tableInput: ({ name, value }, field, decimal = false) => {
          validatePositionAndReplacement(field);
          formChange(name, decimal ? (value && parseInt(value, 10)) || '' : value);
        },
        tableCheckbox: (name, checked) => {
          const { isYesNo = false, selectAll = false } = options || {};
          const isZero = name.match(/[a-zA-Z]+Zeros$/g);

          if (!selectAll) {
            const yesNoValue = checked ? YES : NO;
            const addSeparator = /([a-zA-Z]+)FileSeparatorVisible/g.exec(name);

            if (addSeparator) {
              const separators = apiResponse.separators.source;
              const instance = api.fields.find((x) => camelCase(x.fieldName) === 'sendDate');
              const selected = separators.find((s) => s.id === instance.fileSeparator);
              let value = separators[0]?.id === RESPONSES.NO ? null : separators[0];

              if (selected) {
                value = [selected];
              }

              formChange(`${addSeparator[1]}FileSeparator`, checked ? value : null);
            }

            formChange(name, isYesNo ? yesNoValue : +checked);

            if (!isYesNo) {
              let count = 0;
              Object.keys(formState).forEach((field) => {
                const match = field.match(
                  new RegExp(`[a-zA-Z0-9]+TrimLeading(${isZero ? 'Zeros' : 'Blanks'})`, 'g')
                );
                if (match) {
                  const checked = formState[field] >= 1;
                  if (checked) {
                    count += 1;
                  }
                }
              });

              if (count === api.fields.length) {
                formChange(`trimLeading${isZero ? 'Zeros' : 'Blanks'}`, false);
              }

              if (count + 1 === api.fields.length && checked) {
                formChange(`trimLeading${isZero ? 'Zeros' : 'Blanks'}`, true);
              }
            }
            return;
          }

          formChange(name, checked);
          api.fieldNames.forEach((field) => {
            formChange(`${field}TrimLeading${isZero ? 'Zeros' : 'Blanks'}`, +checked);
          });
        }
      };

      // in case type is not supported
      if (!events[type]) {
        return;
      }

      events[type](data, value, add);
    };

  const getProperReplacementField = ({ charges, f, required }) => {
    const isCharges = f === 'detailOfCharges';
    const isTransactionCurrency = f === 'transactionCurrency';
    const isRequired = required === YES;

    const requiredReplacement = {
      fn: hasEitherPositionOrReplacement(f),
      msg: locale['validation.required']
    };

    const getValidators = (field, isRequired) => {
      const fieldTypes = {
        debitAccountNumber: [
          ...(isRequired ? [requiredReplacement] : []),
          {
            fn: validateAccountNumber,
            msg: locale['wireImports.mapErrors.debitAccountNumber']
          }
        ],
        beneficiaryBankCode: [
          ...(isRequired ? [requiredReplacement] : []),
          {
            fn: validateBankCode,
            msg: locale['wireImports.mapErrors.beneficiaryBankCode']
          }
        ],
        sendDate: [
          ...(isRequired ? [requiredReplacement] : []),
          {
            fn: validateDate,
            msg: locale['wireImports.mapErrors.sendDate']
          }
        ]
      };
      return fieldTypes[field] || [];
    };

    if (isCharges) {
      return (
        <Dropdown
          className={classnames(classes.tableDropdown, classes.hideDropdownLabel, classes.charges)}
          dataQa={`${f}ReplacementValue`}
          defaultValue={formState[`${f}ReplacementValue`]}
          filterEnabled={false}
          htmlId={`${f}ReplacementValue`}
          name={`${f}ReplacementValue`}
          onChange={onChange('dropdown')}
          options={charges.source}
        />
      );
    }

    const field = api.fields.find((x) => camelCase(x.fieldName) === f);

    const showCharCount = field && field.size && !isTransactionCurrency;
    return (
      <Input
        data-qa={`${f}ReplacementValue`}
        disabled={isTransactionCurrency}
        forwardedRef={refs[f]}
        maxLengthIndicatorLimit={showCharCount && field.size}
        name={`${f}ReplacementValue`}
        onChange={(name, value) => {
          validatePositionAndReplacement(f);
          onChange('tableInput')({ name, value }, f);
        }}
        validators={[
          ...getValidators(f, isRequired),
          ...(showCharCount
            ? [
                lt({
                  maxLength: field.size,
                  msg: template(locale['validation.maxNCharacters'])({ max: field.size })
                })
              ]
            : [])
        ]}
        value={formState[f]}
      />
    );
  };

  const onClear = ({ charges, dateFormats, delimiters, endOfRecord, separators }) => {
    Object.keys(api.fields).forEach((k) => {
      const field = camelCase(api.fields[k].fieldName);
      const {
        amountDecimal,
        fieldPosition,
        fileSeparator,
        replacementValue,
        trimLeadingBlanks,
        trimLeadingZeros
      } = api.fields.find((x) => camelCase(x.fieldName) === field);

      const separator = get(fieldsData, `fields.${field}.fileSeparator`, false);
      const special = get(fieldsData, `fields.${field}.specialFormatting`, false);

      const getReplacementValue = () => {
        const isCharges = field === 'detailOfCharges';

        if (isCharges) {
          return replacementValue
            ? [charges.source.find((x) => x.id === replacementValue)]
            : charges[0];
        }

        return replacementValue;
      };

      const matchingSeparator = separators.source.find(
        (x) => x?.name?.toLowerCase() === separator?.toLowerCase()
      );
      const hasValidSeparator = separator && matchingSeparator;

      Object.assign(formState, {
        [`${field}AmountDecimal`]: amountDecimal,
        [`${field}FieldPosition`]: fieldPosition || '',
        [`${field}TrimLeadingBlanks`]: Boolean(trimLeadingBlanks),
        [`${field}TrimLeadingZeros`]: Boolean(trimLeadingZeros),
        [`${field}FileSeparatorVisible`]: !!(
          fileSeparator && fileSeparator.toUpperCase() !== RESPONSES.NO
        ),
        trimLeadingZeros: false,
        trimLeadingBlanks: false,
        delimiter: [delimiters.source[0]],
        mapName: get(api.header, 'mapName', ''),
        skipHeaderRecord: 0,
        stripQuotes: 0,
        endOfRecordMethod: [endOfRecord.source[0]],
        [`${field}ReplacementValue`]: getReplacementValue() || '',
        [`${field}FileSeparator`]: hasValidSeparator ? [matchingSeparator] : [],
        [`${field}SpecialFormatting`]: special
          ? [dateFormats.source.find((x) => x.name.toLowerCase() === special.toLowerCase())]
          : []
      });
    });

    setError(null);
    formChange('clear', true);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { isValid } = await formValidate();

    if (!isValid) {
      setError(locale['validation.required']);
      return false;
    }
    if (duplicates.length > 0) return false;

    setSending(true);
    const payload = {
      wireImportMapDetail: [
        ...api.fields.map((f) => {
          const field = camelCase(f.fieldName);
          const replacementValue = formState[`${field}ReplacementValue`] || null;
          const isCharges = field === 'detailOfCharges';

          return {
            ...f,
            amountDecimal: formState[`${field}AmountDecimal`] || null,
            fieldPosition: (!replacementValue && formState[`${field}FieldPosition`]) || null,
            fileSeparator: formState[`${field}FileSeparator`]?.[0]?.name?.toLowerCase() || 'no',
            replacementValue: isCharges ? replacementValue?.[0]?.id : replacementValue,
            specialFormatting: formState[`${field}SpecialFormatting`]?.[0]?.name || null,
            trimLeadingBlanks: formState[`${field}TrimLeadingBlanks`] ? 1 : 0,
            trimLeadingZeros: formState[`${field}TrimLeadingZeros`] ? 1 : 0
          };
        })
      ],
      wireImportMapHeader: {
        createdBy: modify ? createdBy : user.userCode || null,
        delimiter: formState?.delimiter?.[0]?.code,
        endofRecordMethod: formState?.endOfRecordMethod?.[0]?.code || '',
        fileType: 'Delimited',
        mapName: formState.mapName,
        mapType: 'Payment',
        skipHeaderRecord: formState.skipHeaderRecord || 0,
        stripQuotes: formState.stripQuotes || NO,
        templateType: 'Custom',
        wireType: formState.wireType,
        ...(modify ? { mtMapId: parseInt(id, 10) } : [])
      }
    };

    const api$ = modify ? modifyWireImportMap(payload) : createWireImportMap(payload);
    return api$.subscribe(({ data, success }) => {
      if (!success) {
        const message = get(data, '0.userMessage');
        const code = get(data, '0.code');

        const mappings = {
          'PAYMENTS.INVALID_MAP_NAME': locale['wireImports.apiErrors.invalidMapName'],
          'PAYMENTS.INVALID_REPLACEMENT_VALUE':
            locale['wireImports.apiErrors.noSpecialCharacters'],
          'PAYMENTS.INVALID_FIRST_CHARACTER ': locale['wireImports.apiErrors.removeColonOrHyphen']
        };

        if (message) {
          const pattern = /\\n(.+?)\\n/g;
          const match = pattern.exec(message);
          const field = match && api.fields.find((f) => f.fieldName === match[1]);

          setError({
            message: mappings[code] ? mappings[code] : message,
            field: field && field.displayName
          });
        } else {
          setError(locale['wireImports.mapErrors.default']);
        }

        return;
      }

      history.push({
        pathname: prevUrl,
        state: {
          messages: [
            {
              id: 1,
              modifyOrCreate: true,
              title: success
                ? locale[
                    modify ? 'wireImports.mapHasBeenModified' : 'wireImports.mapHasBeenCreated'
                  ]
                : locale[
                    modify
                      ? 'wireImports.mapHasFailedToModify'
                      : 'wireImports.mapHasFailedToCreate'
                  ],
              data: { mapName: formState.mapName },
              success
            }
          ]
        }
      });
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const services = useMemo(() => [
    getWireTypes,
    getDelimiters,
    getSeparators,
    getDateFormats,
    getEndOfRecordOptions,
    getCharges
  ]);

  // For CreateImportMap Component to understand what paymentType is set (for rendering header text)
  useEffect(() => {
    if (modify) {
      setPaymentType(
        Object.keys(TRANSCRIPTS_WIRE_TYPES).filter(
          (key) => TRANSCRIPTS_WIRE_TYPES[key] === formState.wireType
        )
      );
    }
  }, [modify, setPaymentType, formState.wireType]);

  // For CreateImportMap Component to understand when WIRE'S API is done
  useEffect(() => setLoadingForWiresApi(api.loading), [setLoadingForWiresApi, api.loading]);

  const helpTextMap = {
    [COUNTRY_FIELD]: (
      <ThemedLink
        bare
        className={classes.countryCodeDownloadBtn}
        data-qa="download-country-code-list"
        download={COUNTRY_CODE_LIST}
        title={locale['payeeImport.viewDetails.countryCodeList']}
        to={countryCodeListFiles}
      >
        <ContainerArrowDownNextIcon />
        {locale['payeeImport.viewDetails.countryCodeList']}
      </ThemedLink>
    ),
    [IBAN_ACCOUNT_TYPE]: (
      <Tooltip
        content={
          <span className={classes.ibanAccountType}>
            {locale['payeeImport.map.ibanAccountType']}
          </span>
        }
        tooltipId="iban-account-type-tooltip-content"
      >
        <span className={classes.ibanAccountTypeIcon} data-qa="iban-account-type">
          <InfoCircleAshIcon />
        </span>
      </Tooltip>
    )
  };

  const getHelpText = (fieldName) => helpTextMap[fieldName];

  return (
    <form className={classes.wrapper}>
      <header className={classes.header}>
        <h4 className={classes.subtitle}>{locale['wireImports.delimitedFileMap']}</h4>
      </header>

      {error && (
        <Alert
          autoFocus
          className={classes.alert}
          dataQa="createWireImport-error"
          message={
            error.message && (
              <div>
                <div>
                  {error.message}
                  {error.field ? ' in ' : ''}
                </div>
                {error.field && <div>{`'${error.field}'`}</div>}
              </div>
            )
          }
          onHide={() => setError(null)}
          title={
            modify ? locale['wireImports.canNotModifyMap'] : locale['wireImports.canNotCreateMap']
          }
          variant={ALERTVARIATIONS.ERROR}
        />
      )}

      {modify && wirePreferences?.isoFormatEnabled && !api?.isISOFormatEnabled && (
        <Alert
          className={classes.alert}
          dataQa="createWireImport-iso-address-warning"
          message={locale['wireImports.updateIsoAddress']}
          variant={ALERTVARIATIONS.WARNING}
        />
      )}

      <PlaceResolver services={services} variant={variants.table}>
        {({
          results: [
            { radio },
            { dropdown: delimiters },
            { dropdown: separators },
            { dropdown: dateFormats },
            { dropdown: endOfRecord },
            { dropdown: charges }
          ]
        }) => {
          const separatorsDetail = separators.source.filter(
            (x) => x.id.toUpperCase() !== RESPONSES.NO
          );

          if (!apiResponse) {
            setApiResponse({
              wireTypes: radio.source,
              delimiters,
              separators,
              endOfRecord,
              dateFormats,
              charges
            });
          }

          return (
            <>
              <section className={classes.optionsSection}>
                {/* Wire types */}
                <RadioButton
                  className={classes.hidden}
                  data={radio.source}
                  name="wireType"
                  onUpdate={(data, value, add) => {
                    if (!initial) {
                      setValidations(() => ({ fields: {} }));
                      onClear({
                        delimiters,
                        endOfRecord,
                        dateFormats,
                        charges,
                        separators
                      });
                    }
                    onChange('radio')(data, value, add);
                  }}
                  settings={{
                    titleOnTop: true
                  }}
                  style
                  styled
                  title={locale['wireImports.selectPaymentType']}
                  value={!modify ? formState.wireType || wireType : radio.preselected}
                />

                {/* Map name */}
                <Input
                  className={classnames('form-group', classes.mapName)}
                  data-qa="mapName"
                  labelText={locale['wireImports.mapName']}
                  maxLengthIndicatorLimit={25}
                  name="mapName"
                  onChange={onChange('input')}
                  validateOnBlur
                  validators={[
                    { required: true, msg: locale['validation.required'] },
                    {
                      fn: (name, _, value) => validateMapName(name, value, api),
                      msg: locale['wireImports.mapNameAlreadyUsed']
                    },
                    lt({
                      maxLength: 25,
                      msg: template(locale['validation.maxNCharacters'])({ max: 25 })
                    })
                  ]}
                  value={formState.mapName}
                />

                {/* Delimiter */}
                <Dropdown
                  className={classes.delimiters}
                  dataQa="map-delimiter"
                  filterEnabled={false}
                  htmlId="map-delimiter"
                  labelText={locale['common.delimiter']}
                  name="delimiter"
                  onChange={onChange('dropdown')}
                  options={delimiters.source}
                  validators={[
                    { required: true, msg: locale['validation.required'] },
                    {
                      fn: (name, param, data) => {
                        const currValue = get(data, `${name}.0.id`);
                        const endOfRecordValue = get(data, 'endOfRecordMethod.0.id');
                        return currValue !== endOfRecordValue;
                      },
                      msg: locale['wireImports.mapErrors.DELIMITER_AND_END_OF_RECORD_MISMATCH']
                    }
                  ]}
                />

                <Dropdown
                  className={classes.delimiters}
                  dataQa="map-eorMethod"
                  filterEnabled={false}
                  htmlId="map-eorMethod"
                  labelText={locale['wireImports.endOfRecordMethod']}
                  name="endOfRecordMethod"
                  onChange={onChange('dropdown')}
                  options={endOfRecord.source}
                  validators={[
                    { required: true, msg: locale['validation.required'] },
                    {
                      fn: (name, param, data) => {
                        const currValue = get(data, `${name}.0.id`);
                        const delimiterValue = get(data, 'delimiter.0.id');
                        return currValue !== delimiterValue;
                      },
                      msg: locale['wireImports.mapErrors.DELIMITER_AND_END_OF_RECORD_MISMATCH']
                    }
                  ]}
                />

                {/* Options */}
                <div className={classes.extraOptions}>
                  <div>
                    <Checkbox
                      checked={!!formState.skipHeaderRecord}
                      className={classes.tableCheckbox}
                      data-qa="skipHeaderRecord"
                      htmlId="skipHeaderRecord"
                      labelText={locale['wireImports.skipHeaderRecords']}
                      name="skipHeaderRecord"
                      onChange={onChange('checkbox')}
                    />

                    {formState.skipHeaderRecord > 0 && (
                      <Input
                        className={classes.countInput}
                        data-qa="skipHeaderRecordValue"
                        inputMode="numeric"
                        label={locale['wireImports.count']}
                        name="skipHeaderRecordValue"
                        onChange={onChange('input')}
                        validateBlurOnly
                        validators={[
                          { required: true, msg: locale['validation.required'] },
                          {
                            fn: (name, param, data) => Number(data[name]) >= 1,
                            msg: template(locale['common.greaterThan'])({ field: 0 })
                          }
                        ]}
                        value={formState.skipHeaderRecordValue}
                      />
                    )}
                  </div>
                  <div>
                    <Checkbox
                      checked={formState.stripQuotes === YES}
                      className={classes.tableCheckbox}
                      data-qa="stripQuotes"
                      htmlId="stripQuotes"
                      labelText={locale['wireImports.stripQuotesAroundFields']}
                      name="stripQuotes"
                      onChange={onChange('checkbox', { isYesNo: true })}
                    />
                  </div>
                </div>
              </section>
              <section className={classes.tableContent}>
                {api.loading && (
                  <div className={classes.spinner}>
                    <Spinner />
                  </div>
                )}

                {!api.loading && (
                  <table className={classes.table}>
                    <thead className={classes.tableThead}>
                      <tr className={classes.columnGroupHeading}>
                        <th />
                        <th colSpan="2">{locale['wireImports.groupTitle.trimLeading']}</th>
                      </tr>
                      <tr className={classes.tableHeading}>
                        <th>{locale['importMapping.title.fieldName']}</th>
                        <th>{locale['wireImports.title.blanks']}</th>
                        <th>{locale['wireImports.title.zeros']}</th>
                        <th>{locale['importMapping.title.specialFormatting']}</th>
                        <th>{locale['importMapping.title.fieldPosition']}</th>
                        <th className={classes.replacementValue}>
                          {locale['importMapping.title.replacementValue']}
                        </th>
                      </tr>
                      <tr className={classes.headingActions}>
                        <th>{locale['common.selectAll']}</th>
                        <th>
                          <Checkbox
                            checked={formState.trimLeadingBlanks}
                            data-qa="trimLeadingBlanks"
                            htmlId="trimLeadingBlanks"
                            name="trimLeadingBlanks"
                            onChange={onChange('tableCheckbox', {
                              selectAll: 'trimLeadingBlanks'
                            })}
                          />
                        </th>
                        <th>
                          <Checkbox
                            checked={formState.trimLeadingZeros}
                            data-qa="trimLeadingZeros"
                            htmlId="trimLeadingZeros"
                            name="trimLeadingZeros"
                            onChange={onChange('tableCheckbox', { selectAll: 'zeros' })}
                          />
                        </th>
                        <th />
                        <th className={classes.tableHeadingDesc} colSpan="2">
                          {locale['wireImports.messageRow.message']}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(fieldsData.fields).map((f) => {
                        const field = fieldsData.fields[f];
                        const visible = field.isDisplay === YES;
                        const duplicate = get(
                          validations,
                          `fields.${camelCase(field.fieldName)}FieldPosition.duplicate`,
                          false
                        );

                        const required = {
                          position: get(
                            validations,
                            `fields.${camelCase(field.fieldName)}FieldPosition.required`,
                            false
                          ),
                          replacement: get(
                            validations,
                            `fields.${camelCase(field.fieldName)}ReplacementValue.required`,
                            false
                          )
                        };

                        if (!visible) {
                          return null;
                        }

                        return (
                          <tr
                            key={`wireImportField-${field.fieldName}`}
                            data-qa={`field-row-${field.fieldName
                              ?.replace(/\s/g, '-')
                              .toLowerCase()}`}
                          >
                            <td
                              className={classnames({
                                [classes.tableFieldOptional]: field.required !== YES
                              })}
                            >
                              {`${field.displayName} ${field.size !== 0 ? `(${field.size})` : ''}`}
                              {getHelpText(field.fieldName)}
                            </td>
                            <td>
                              <Checkbox
                                checked={!!formState[`${f}TrimLeadingBlanks`]}
                                className={classes.tableCheckbox}
                                data-qa={`${f}TrimLeadingBlanks`}
                                htmlId={`${f}TrimLeadingBlanks`}
                                name={`${f}TrimLeadingBlanks`}
                                onChange={onChange('tableCheckbox')}
                              />
                            </td>
                            <td>
                              <Checkbox
                                checked={!!formState[`${f}TrimLeadingZeros`]}
                                className={classes.tableCheckbox}
                                data-qa={`${f}TrimLeadingZeros`}
                                htmlId={`${f}TrimLeadingZeros`}
                                name={`${f}TrimLeadingZeros`}
                                onChange={onChange('tableCheckbox')}
                              />
                            </td>
                            <td>
                              {field.amountDecimal && (
                                <Checkbox
                                  checked={formState[`${f}AmountDecimal`] === YES}
                                  className={classes.tableCheckbox}
                                  data-qa={`${f}AmountDecimal`}
                                  htmlId={`${f}AmountDecimal`}
                                  labelText={locale['wireImports.useDecimal']}
                                  name={`${f}AmountDecimal`}
                                  onChange={onChange('tableCheckbox', { isYesNo: true })}
                                />
                              )}

                              {field.specialFormatting && (
                                <div>
                                  <Dropdown
                                    className={classnames(
                                      classes.tableDropdown,
                                      classes.hideDropdownLabel
                                    )}
                                    dataQa={`${f}SpecialFormatting`}
                                    defaultValue={formState[`${f}SpecialFormatting`]}
                                    filterEnabled={false}
                                    htmlId={`${f}SpecialFormatting`}
                                    name={`${f}SpecialFormatting`}
                                    onChange={onChange('dropdown')}
                                    options={dateFormats.source}
                                    validators={[
                                      {
                                        required: field.required === YES,
                                        msg: locale['validation.required']
                                      }
                                    ]}
                                  />
                                </div>
                              )}

                              {field.fileSeparator && (
                                <div>
                                  <Checkbox
                                    checked={Boolean(
                                      formState[`${f}FileSeparatorVisible`] &&
                                        formState[`${f}FileSeparatorVisible`]
                                    )}
                                    className={classes.tableCheckbox}
                                    data-qa={`${f}FileSeparatorVisible`}
                                    htmlId={`${f}FileSeparatorVisible`}
                                    labelText={locale['wireImports.addSeparator']}
                                    name={`${f}FileSeparatorVisible`}
                                    onChange={onChange('tableCheckbox')}
                                  />

                                  {formState[`${f}FileSeparatorVisible`] > 0 && (
                                    <Dropdown
                                      className={classnames(
                                        classes.tableDropdown,
                                        classes.hideDropdownLabel,
                                        classes.dropdownTopPadding
                                      )}
                                      dataQa={`${f}FileSeparator`}
                                      defaultValue={[
                                        separatorsDetail.find(
                                          (x) =>
                                            x.name.toLowerCase() ===
                                            fieldsData?.fields?.[f]?.fileSeparator.toLowerCase()
                                        ) || separatorsDetail[0]
                                      ]}
                                      filterEnabled={false}
                                      htmlId={`${f}FileSeparator`}
                                      name={`${f}FileSeparator`}
                                      onChange={onChange('dropdown')}
                                      options={separatorsDetail}
                                    />
                                  )}
                                </div>
                              )}
                            </td>
                            <td
                              className={classnames(classes.fieldPosition, classes.tableInput, {
                                [classes.hasError]: required.position || duplicate
                              })}
                            >
                              <Input
                                data-qa={`${f}-FieldPosition`}
                                error={
                                  duplicates.includes(`${f}FieldPosition`) &&
                                  locale['validation.duplicateField']
                                }
                                inputMode="numeric"
                                name={`${f}FieldPosition`}
                                onChange={(name, value) => {
                                  onChange('tableInput')(
                                    { name, value },
                                    camelCase(field.fieldName),
                                    true
                                  );
                                }}
                                validators={
                                  field.required === YES
                                    ? [
                                        {
                                          fn: hasEitherPositionOrReplacement(f),
                                          msg: locale['validation.required']
                                        }
                                      ]
                                    : []
                                }
                                value={formState[f]}
                              />
                            </td>
                            <td
                              className={classnames(classes.tableInput, classes.replacementValue, {
                                [classes.hasError]: required.replacement
                              })}
                            >
                              {field.isReplacementValue === YES && (
                                <>
                                  {getProperReplacementField({
                                    charges,
                                    required: field.required,
                                    f
                                  })}
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </section>

              {!api.loading && (
                <section className={classes.footer}>
                  <Button
                    className="btn btn-primary"
                    dataQa="save-button"
                    onClick={onSubmit}
                    type="submit"
                  >
                    {locale['common.save']}
                  </Button>

                  <Button
                    className="btn"
                    onClick={() => history.push({ pathname: prevUrl })}
                    variant={SECONDARY}
                  >
                    {locale['common.cancel']}
                  </Button>

                  <Button
                    data-qa="clear-button"
                    onClick={() =>
                      onClear({
                        delimiters,
                        endOfRecord,
                        dateFormats,
                        charges,
                        separators
                      })
                    }
                    variant={TERTIARY}
                  >
                    {locale['common.clear']}
                  </Button>
                </section>
              )}
            </>
          );
        }}
      </PlaceResolver>
    </form>
  );
};

CreateWireImportMapComponent.propTypes = {
  validate: PropTypes.shape({}).isRequired,
  data: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  setSending: PropTypes.func,
  modify: PropTypes.bool,
  user: PropTypes.shape({}).isRequired,
  wireType: PropTypes.string.isRequired,
  setPaymentType: PropTypes.func,
  setLoadingForWiresApi: PropTypes.func,
  setLeavingPage: PropTypes.func
};

/* istanbul ignore next */
const mapStateToProps = (state) => ({
  user: state.auth.user
});

export default connect(mapStateToProps, null)(withFormProvider(CreateWireImportMapComponent));
